<template>
  <div id="app" class="App" :class="isIOS && 'is-ios'">
    <app-header v-if="showHeader" />
    <app-sidebar v-if="showSidebar" />
    <app-modal v-if="showModal" />
    <app-banner v-if="showBanner" />
    <!-- P1: student-only, mobile-only bottom nav. Guarded explicitly by
         student acting context (never renders for a volunteer/admin), so the
         existing teacher/volunteer shell is untouched. -->
    <!-- P41 QA-40: the floating bar's own band of page background, behind
         it from its top edge to the screen's bottom edge (safe area
         included), so scrolling content never shows in the gaps around and
         under the bar. The bar itself is unchanged. -->
    <div
      v-if="showStudentBottomNav"
      class="App-bottom-nav-backdrop"
      aria-hidden="true"
      data-testid="student-bottom-nav-backdrop"
    />
    <student-bottom-nav v-if="showStudentBottomNav" />
    <!--
      Borna bug batch 1 (items 4 + 8): the ONE logout confirmation, rendered
      once at the shell root so the sidebar row and the mobile bottom-nav item
      raise the same dialog and run the same `AuthService.logout`.
    -->
    <logout-confirm-dialog
      v-if="showLogoutConfirm"
      @confirm="confirmLogout"
      @cancel="cancelLogout"
    />
    <!--
      SX-02 (doc 22 §3): the ONE Student Coming Soon dialog, rendered once at
      the shell root and raised through `app/comingSoon` by every gated
      teacher/volunteer entry point and (SX-03B) unavailable course tiles.
      Mounted here -- outside every route -- so it is available on the
      logged-out role-selection screen as well as inside the Student shell.
    -->
    <support-notice />
    <student-coming-soon-dialog
      v-if="showComingSoon"
      :variant="comingSoon.variant"
      :reason="comingSoon.reason"
      :course-label="comingSoon.courseLabel"
      :show-tutor-action="comingSoonOffersTutor"
      :teacher-available="teacherExperienceAvailable"
      @close="hideComingSoon"
      @tutor="goToAiTutorFromComingSoon"
      @teacher="continueWithTeacherFromComingSoon"
    />
    <!--
      Borna bug batch 1 (item 1): the application update/refresh banner is
      GONE -- the `b-alert` that sat here, the 10-minute `vue-crono` poll that
      raised it, and the server-version state it compared against. Deliberately
      not replaced with any other update prompt.
    -->

    <div
      :class="{
        'App-router-view-wrapper': true,
        'App-router-view-wrapper--header': showHeader,
        'App-router-view-wrapper--sidebar': showSidebar,
        // WP-04V-C (root cause R4): the student sidebar is 246px wide but the
        // shared width map reserves 250px, so 4px of legacy page background
        // showed as a seam down the full height of every student screen.
        'App-router-view-wrapper--student-sidebar':
          showSidebar && showStudentShell,
        'App-router-view-wrapper--student-bottom-nav': showStudentBottomNav,
      }"
    >
      <router-view />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import '@/scss/main.scss'
import AppHeader from './AppHeader/index.vue'
import AppSidebar from './AppSidebar/index.vue'
import AppModal from './AppModal/index.vue'
import AppBanner from './AppBanner/index.vue'
import StudentBottomNav from './StudentBottomNav.vue'
import LogoutConfirmDialog from '@/components/StudentShell/LogoutConfirmDialog.vue'
import StudentComingSoonDialog from '@/components/StudentShell/StudentComingSoonDialog.vue'
import SupportNotice from './SupportNotice.vue'
import AuthService from '@/services/AuthService'
import { setLauncherAllowed, setLauncherRaised } from '@/services/SynovaWidget'
import PortalService from '@/services/PortalService'
import getOperatingSystem from '@/utils/get-operating-system'
import AnalyticsService from '@/services/AnalyticsService'
import LoggerService from '@/services/LoggerService'
import i18n from '@/i18n'

export default {
  name: 'App',
  components: {
    AppHeader,
    AppSidebar,
    AppModal,
    AppBanner,
    StudentBottomNav,
    LogoutConfirmDialog,
    StudentComingSoonDialog,
    SupportNotice,
  },
  data() {
    return {
      isIOS: false,
      docHiddenProperty: '',
    }
  },
  async created() {
    // Listen for resize event
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
    await this.$store.dispatch('app/checkEnvironment', this)
    PortalService.call('app.isLoaded')

    this.setVisibilityListener()

    if (this.isMobileApp) {
      document.addEventListener('click', this.handleExternalURLs, false)

      // Borna bug batch 1: the app-upgrade prompt (`UpgradeAppModal`, raised
      // here for an outdated native wrapper) is removed with the rest of the
      // install/update-app prompts. Not replaced.

      if (getOperatingSystem() === 'iOS') {
        this.isIOS = true
        document.addEventListener('click', this.iOSFocusElements, false)
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)

    if (this.isMobileApp) {
      document.removeEventListener('click', this.handleExternalURLs)

      if (getOperatingSystem() === 'iOS') {
        document.addEventListener('click', this.iOSFocusElements, false)
      }
    }
  },
  methods: {
    // Borna bug batch 1 (item 4). Cancel simply closes and leaves the user
    // exactly where they were. Confirm runs the ONE existing logout
    // implementation -- no second copy, no auth/session semantics changed --
    // which now lands on the new Borna login instead of the legacy
    // "you are logged out" page.
    confirmLogout() {
      this.$store.dispatch('app/logoutConfirm/hide')
      AuthService.logout(this)
    },
    cancelLogout() {
      this.$store.dispatch('app/logoutConfirm/hide')
    },
    // SX-02: the shell owns what the Coming Soon dialog's answers DO; the
    // component only reports them (same boundary as LogoutConfirmDialog).
    hideComingSoon() {
      this.$store.dispatch('app/comingSoon/hide')
    },
    goToAiTutorFromComingSoon() {
      this.$store.dispatch('app/comingSoon/hide')
      const target = `/${i18n.locale}/student/ai-tutor`
      if (this.$route && this.$route.path === target) return
      this.$router.push(target).catch(() => {})
    },
    // V405-C (doc 26 §7): the Advisor popup's «ادامه با معلم». The store
    // decides by the central teacher capability; only when it is ON does the
    // shell navigate, to the same request flow the Home teacher card links to.
    async continueWithTeacherFromComingSoon() {
      const navigate = await this.$store.dispatch(
        'app/comingSoon/continueWithTeacher'
      )
      if (!navigate) return
      this.$router.push(`/${i18n.locale}/help/request`).catch(() => {})
    },
    iOSFocusElements(e) {
      if (!e) {
        return
      }
      const focusTags = ['INPUT', 'TEXTAREA', 'SELECT']
      const focusTag = document.activeElement.tagName
      if (
        focusTags.indexOf(focusTag) !== -1 &&
        focusTags.indexOf(e.target.tagName) === -1
      ) {
        return document.activeElement.blur()
      }
    },
    handleResize() {
      this.$store.dispatch('app/windowResize', {
        width: window.innerWidth,
        height: window.innerHeight,
      })
    },
    handleExternalURLs(event) {
      const isATag = event.target.tagName.toLowerCase() === 'a'
      const isSameHost = window.location.hostname === event.target.hostname
      const mailToLinkPattern = /^mailto:/i

      // Open native mobile browser or email if the a tag has a different host or
      // if the a tag has "mailto" in its href
      if (
        isATag &&
        (!isSameHost || mailToLinkPattern.test(event.srcElement.href))
      ) {
        const { href } = event.target
        PortalService.call('browser.openWindow', {
          url: href,
          target: '_system',
        })
      }
    },
    isExemptSocketError(error) {
      return (
        error.message === 'xhr poll error' ||
        error.message === 'websocket error'
      )
    },
    setVisibilityListener() {
      let visibilityChange
      // Opera 12.10 and Firefox 18 and later support
      if (typeof document.hidden !== 'undefined') {
        this.docHiddenProperty = 'hidden'
        visibilityChange = 'visibilitychange'
      } else if (typeof document.msHidden !== 'undefined') {
        this.docHiddenProperty = 'msHidden'
        visibilityChange = 'msvisibilitychange'
      } else if (typeof document.webkitHidden !== 'undefined') {
        this.docHiddenProperty = 'webkitHidden'
        visibilityChange = 'webkitvisibilitychange'
      }

      document.addEventListener(visibilityChange, this.handleVisibilityChange)
    },
    async handleVisibilityChange() {
      await this.$store.dispatch(
        'app/updateWebPageVisibility',
        this.docHiddenProperty
      )
    },
  },
  computed: {
    ...mapState({
      showHeaderRaw: state => state.app.header.isShown,
      headerComponent: state => state.app.header.component,
      showSidebar: state => state.app.sidebar.isShown,
      showModal: state => state.app.modal.isShown,
      showBanner: state => state.app.banner.isShown,
      showLogoutConfirm: state => state.app.logoutConfirm.isShown,
      showComingSoon: state => state.app.comingSoon.isShown,
      comingSoon: state => state.app.comingSoon,
      isMobileApp: state => state.app.isMobileApp,
      isWebPageHidden: state => state.app.isWebPageHidden,
      user: state => state.user.user,
      session: state => state.user.session,
      subjects: state => state.subjects.subjects,
    }),
    ...mapGetters({
      userAuthenticated: 'user/isAuthenticated',
      isVolunteer: 'user/isVolunteer',
      mobileMode: 'app/mobileMode',
      isAutoFlowUser: 'user/isAutoFlowUser',
      getUserPropsForAnalytics: 'user/getUserPropsForAnalytics',
      teacherExperienceAvailable:
        'featureFlags/isStudentTeacherExperienceAvailable',
    }),
    // P3 final shell cleanup (00_*.md §13.x, PF-15 defect 1): router.js's
    // blanket `app/showNavigation` (dispatched on every route change, see
    // `router.js`'s global afterEach) defaults the legacy AppHeader to
    // DefaultHeader -- the small circular Borna logo -- with no student
    // awareness. On the Dashboard route this duplicated the new
    // StudentAppHeader (DashboardView/index.vue) with an extra blank
    // masthead strip above it (the human's exact annotated defect).
    //
    // Real functional header variants (RejoinSessionHeader, VerificationHeader,
    // BannedHeader, DashboardBannerHeader -- all set `header.component` to a
    // specific name from StudentDashboard/index.vue) are genuine content a
    // student must still see on this same route, so only the *generic,
    // componentless* case is suppressed, and only on routes that render
    // their own `StudentAppHeader` for an active student. Every other
    // student route and the entire volunteer/teacher shell (which always
    // has `headerComponent` falsy too, but is never on these routes with
    // `!isVolunteer`) is unaffected.
    //
    // P4 AI Tutor WP-02C-JOURNEY-PARITY (doc 11, 2026-09-17): extended from
    // 'DashboardView'-only to also cover 'StudentAiTutorView', which now
    // renders its own `student-app-header` the same way DashboardView does
    // (StudentAiTutorView.vue) -- without this, that route fell back to the
    // legacy global masthead instead of the already-accepted shell.
    //
    // WP-04V final closeout: extended again to 'SessionHistoryView' (Reports)
    // and 'ProfileView', which now render their own `student-app-header` for a
    // non-volunteer. The human's UAT found both still showing the legacy
    // masthead. This list must stay identical to `AppSidebar/index.vue`'s.
    //
    // WB-M1A: 'StudentAiTutorBoardView' (the board Workspace) renders its own
    // `student-app-header` too, with the credit pill.
    showHeader() {
      if (!this.showHeaderRaw) return false
      const rendersOwnStudentAppHeader =
        this.$route.name === 'DashboardView' ||
        this.$route.name === 'StudentAiTutorView' ||
        this.$route.name === 'StudentAiTutorBoardView' ||
        this.$route.name === 'SessionHistoryView' ||
        this.$route.name === 'ProfileView'
      const isStudentGenericMasthead =
        rendersOwnStudentAppHeader && !this.isVolunteer && !this.headerComponent
      return !isStudentGenericMasthead
    },
    // P1: same student-acting-context gate as SidebarLinks' isMinimalHomeStudent
    // (authenticated, not a volunteer, not an admin), plus mobile-only. False
    // for any volunteer/admin session, so their shell is never touched.
    showStudentBottomNav() {
      return (
        this.userAuthenticated &&
        !this.isVolunteer &&
        !this.user.isAdmin &&
        this.mobileMode
      )
    },
    // WP-04V-C: the same student-acting-context gate `AppSidebar` uses for
    // `AppSidebar--student` (authenticated, not a volunteer), so the wrapper's
    // reserved width and the sidebar's actual width can never disagree again.
    showStudentShell() {
      return this.userAuthenticated && !this.isVolunteer
    },
    // Batch 45: the native Synova button is for students and for logged-out
    // pages, where the role is not known yet. A volunteer or admin shell is
    // unchanged and keeps its own contact row.
    synovaLauncherAllowed() {
      return (
        !this.userAuthenticated || (!this.isVolunteer && !this.user.isAdmin)
      )
    },
    synovaLauncherRaised() {
      return !!this.$route && this.$route.name === 'StudentAiTutorView'
    },
    // SX-02: the "start with the AI tutor" action only makes sense for an
    // authenticated student who is not already on the tutor route, and only
    // for the teacher-HELP variant (registration copy offers no alternative).
    // V405-C: the Advisor popup offers it on the same terms.
    comingSoonOffersTutor() {
      const offersAlternative =
        this.comingSoon.variant === 'advisor' ||
        (this.comingSoon.variant === 'teacher' &&
          this.comingSoon.reason === 'help')
      return (
        offersAlternative &&
        this.userAuthenticated &&
        !this.isVolunteer &&
        this.$route.name !== 'StudentAiTutorView'
      )
    },
  },
  watch: {
    // SX-02 (doc 22 §3): two shell-root dialogs are never active at once.
    // `app/comingSoon/show` already hides the logout confirmation; this covers
    // the other direction without touching the accepted logoutConfirm module.
    showLogoutConfirm(isShown) {
      if (isShown && this.showComingSoon) {
        this.$store.dispatch('app/comingSoon/hide')
      }
    },
    user(currentUserValue, previousUserValue) {
      const nowLoggedIn = currentUserValue.id && !previousUserValue.id
      if (nowLoggedIn) {
        const userProps = this.getUserPropsForAnalytics
        AnalyticsService.identify(currentUserValue.id, userProps)
        LoggerService.identify(currentUserValue._id)

        // Borna bug batch 1 (item 2): the mobile install-app prompt
        // (`MobileAppNoticeBanner`) used to be raised here on first login for
        // every mobile-web student. It is removed, not replaced.

        this.$store.dispatch('productFlags/getUserProductFlags')
        if (Object.entries(this.subjects).length === 0)
          this.$store.dispatch('subjects/getSubjects')
      } else if (currentUserValue.id) {
        const userProps = this.getUserPropsForAnalytics
        AnalyticsService.updateUser(userProps)
      }
    },
    /**
     * On route transition, fetch current session (for students, also most recent session)
     * @todo: Fetch these much less frequently (only once).
     * @todo: Consolidate with the logic in router.
     */
    $route() {
      if (this.userAuthenticated) {
        this.$store.dispatch('user/fetchSession', this)

        if (!this.isVolunteer) {
          this.$store.dispatch('user/fetchLatestSession', this)
        }
      }

      document.querySelector('html').setAttribute('lang', this.$i18n.locale)
      if (this.$i18n.locale == 'fa') {
        document.querySelector('html').classList.add('is-rtl')
        document.querySelector('html').setAttribute('dir', 'rtl')
        this.$store.commit('app/setDirection', 'rtl')
      } else {
        document.querySelector('html').classList.remove('is-rtl')
        document.querySelector('html').setAttribute('dir', 'ltr')
        this.$store.commit('app/setDirection', 'ltr')
      }
    },
    synovaLauncherAllowed: {
      immediate: true,
      handler: setLauncherAllowed,
    },
    synovaLauncherRaised: {
      immediate: true,
      handler: setLauncherRaised,
    },
    // isAutoFlowUser(currentValue, prevValue) {
    //   if (currentValue && !prevValue)
    //     this.$router.push(`/${i18n.locale}/welcome`)
    // },
    session(currentValue, prevValue) {
      const hadASession = !currentValue.id && prevValue.id
      if (hadASession) {
        this.$store.dispatch('user/updateHadASession', true)
        this.$store.dispatch(
          'user/updatePrevSessionSubject',
          prevValue.subTopic
        )
      }
    },
  },
  sockets: {
    error(error) {
      if (this.isExemptSocketError(error)) return
      LoggerService.noticeError(error)
    },
    // https://socket.io/docs/v2/client-api/#event-disconnect
    async disconnect(reason) {
      const err = new Error(
        this.$root.$t('app.disconnectError', {
          userId: this.user.id,
          reason: reason,
        })
      )
      LoggerService.noticeError(err)

      if (reason === 'io server disconnect') {
        // the disconnection was initiated by the server, you need to reconnect manually
        if (!this.$socket.connected) await this.$socket.connect()
      }
    },
    connect_error(error) {
      // these are handled internally and shouldn't be forwarded to Sentry
      if (this.isExemptSocketError(error)) return
      LoggerService.noticeError(error)
    },
    reconnect_error(error) {
      if (this.isExemptSocketError(error)) return
      LoggerService.noticeError(error)
    },
    'session-change'(sessionData) {
      this.$store.dispatch('user/updateSession', sessionData)
    },
    redirect() {
      this.$router.push(`/${i18n.locale}/dashboard`)
    },
  },
}
</script>

<style lang="scss" scoped>
.App {
  height: 100%;
}

.is-ios {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  overflow: auto;

  @media only screen and (orientation: landscape) {
    min-height: initial;
  }
}

.App-router-view-wrapper {
  height: 100%;

  &--header {
    @include bind-app-header-height(padding-top);
  }

  &--sidebar {
    @include bind-app-sidebar-width(padding-left);
    padding-left: 0;
  }

  // WP-04V-C (R4): declared after the mixin so it wins at desktop -- Sass
  // hoists the mixin's media block below these declarations, which is why the
  // `padding-left: 0` above does not. LTR here; the RTL mirror is at the
  // bottom of this block.
  &--student-sidebar {
    @include borna-desktop {
      padding-left: $borna-sidebar-width;
    }
  }

  // P41 QA-40: see `.App-bottom-nav-backdrop` -- this padding keeps the LAST
  // content clear of the bar; the band keeps the rest from showing around it.
  // P1: keeps page content from sitting behind the fixed student bottom nav.
  // WP-04V-D (R8): derived from the bar's own token instead of the hardcoded
  // 56px, which never matched the bar it was clearing.
  &--student-bottom-nav {
    padding-bottom: calc(
      #{$borna-bottom-nav-clearance} + env(safe-area-inset-bottom)
    );
  }
}

// P41 QA-40: the band behind the floating student bottom nav (the bar is
// z-index 40, inset 10px, 63px high). It is the page's own background
// ($borna-canvas, measured on every student page), from the bar's top edge
// to the bottom of the screen, safe area included -- so the 10px gaps
// beside and under the bar, and the bar's slightly translucent glass, show
// page background instead of the content scrolling under them. It steps
// aside with the bar while the Student types (P40 QA-47).
.App-bottom-nav-backdrop {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 39;
  height: calc(#{$borna-bottom-nav-clearance} + env(safe-area-inset-bottom));
  background: $borna-canvas;
}

html.borna-composer-typing .App-bottom-nav-backdrop {
  display: none;
}

html.is-rtl * {
  direction: rtl;
}

html.is-rtl {
  .is-ios {
    right: 0;
    left: unset;
  }

  .App-router-view-wrapper {
    &--sidebar {
      @include bind-app-sidebar-width(padding-right);
      padding-right: 0;
    }

    // WP-04V-C (R4): the RTL mirror of the student width correction. 246px is
    // the sidebar's own measured width, so the content area now ends exactly
    // where the sidebar begins and the 4px seam is gone.
    &--student-sidebar {
      @include borna-desktop {
        padding-right: $borna-sidebar-width;
      }
    }

    padding-left: unset;
  }
}
</style>

<style lang="scss">
// Batch 45: the loader sets the button's inline styles itself, including
// `display` while the panel is open, so the gate is `visibility`. Hidden until
// the widget is really ready (services/SynovaWidget.js), and out of the way
// while the student types, as the bottom nav is.
html:not(.borna-synova-ready) #synova-floating-button,
html:not(.borna-synova-allowed) #synova-floating-button,
html.borna-composer-typing #synova-floating-button {
  visibility: hidden !important;
  pointer-events: none !important;
}

// Measured at 390 (doc 45): 11 px clear of the bottom nav, which moves with the
// safe area, and on the Tutor route 11 px clear of the docked composer. Just above
// the nav (40) and under every menu, sheet and dialog (44 and up): a phone hides
// this button while the panel is open, so it never has to sit above the panel.
@media (max-width: 768px) {
  #synova-floating-button {
    z-index: 41 !important;
    bottom: calc(
      #{$borna-bottom-nav-clearance} + 11px + env(safe-area-inset-bottom)
    ) !important;
  }

  html.borna-synova-raised #synova-floating-button {
    bottom: calc(148px + env(safe-area-inset-bottom)) !important;
  }
}
</style>
